import { useMemo } from 'react';
import { Botao, Status } from '../../componentes/ui.jsx';
import './calendario.css';

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MAX_NA_CELULA = 3;

/** Primeiro e ultimo dia do mes de uma data AAAA-MM-DD. */
export function limitesDoMes(dataISO) {
  const [a, m] = dataISO.split('-').map(Number);
  const pad = (n) => String(n).padStart(2, '0');
  const ultimo = new Date(a, m, 0).getDate();
  return { de: `${a}-${pad(m)}-01`, ate: `${a}-${pad(m)}-${pad(ultimo)}` };
}

/** Soma meses a uma data, caindo no dia 1 (evita "31 de fevereiro"). */
export function somarMeses(dataISO, n) {
  const [a, m] = dataISO.split('-').map(Number);
  const d = new Date(a, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

const isoDe = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ATIVOS = new Set(['pre_agendado', 'pendente', 'confirmado', 'em_andamento']);

function diaPorExtenso(iso) {
  const t = new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Mes inteiro + o dia escolhido ao lado.
 *
 * A CELULA INTEIRA escolhe o dia (antes so o numerozinho fazia isso, e os
 * horarios dentro da celula abriam a OS no lugar: clicar no dia era mirar).
 * Os horarios na celula sao so uma previa; o painel ao lado lista o dia todo
 * e e de la que se abre a OS, marca um horario novo ou vai para a lista.
 * Duplo clique no dia abre a lista daquele dia.
 *
 * Os agendamentos ja chegam com `data` no fuso da empresa: aqui so agrupamos.
 */
export function Calendario({ mes, agendamentos, hoje, diaEscolhido, aoEscolherDia, aoAbrirDia, aoEscolherOS, aoMarcar }) {
  const porDia = useMemo(() => {
    const mapa = new Map();
    for (const a of [...agendamentos].sort((x, y) => x.horaInicio.localeCompare(y.horaInicio))) {
      if (!mapa.has(a.data)) mapa.set(a.data, []);
      mapa.get(a.data).push(a);
    }
    return mapa;
  }, [agendamentos]);

  // Semanas completas (segunda a domingo), com os dias do mes vizinho apagados.
  const celulas = useMemo(() => {
    const [ano, m] = mes.split('-').map(Number);
    const primeiro = new Date(ano, m - 1, 1);
    const recuo = (primeiro.getDay() + 6) % 7;
    const inicio = new Date(ano, m - 1, 1 - recuo);
    const total = new Date(ano, m, 0).getDate();
    const quantas = Math.ceil((recuo + total) / 7) * 7;
    return Array.from({ length: quantas }, (_, i) => {
      const d = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i);
      return { iso: isoDe(d), dia: d.getDate(), doMes: d.getMonth() === m - 1, fimDeSemana: d.getDay() === 0 || d.getDay() === 6 };
    });
  }, [mes]);

  const doDiaEscolhido = porDia.get(diaEscolhido) ?? [];
  const ativosNoDia = doDiaEscolhido.filter((a) => ATIVOS.has(a.status)).length;

  return (
    <div className="cal">
      <div className="cal__grade" role="grid" aria-label="Agenda do mês">
        {DIAS_SEMANA.map((d) => (
          <div key={d} className="cal__semana" role="columnheader">
            {d}
          </div>
        ))}
        {celulas.map((c) => {
          const doDia = porDia.get(c.iso) ?? [];
          const classes = [
            'cal__dia',
            !c.doMes && 'cal__dia--fora',
            c.fimDeSemana && 'cal__dia--fds',
            c.iso < hoje && 'cal__dia--passado',
            c.iso === diaEscolhido && 'cal__dia--sel'
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={c.iso}
              role="gridcell"
              tabIndex={c.iso === diaEscolhido ? 0 : -1}
              aria-selected={c.iso === diaEscolhido}
              aria-label={`${diaPorExtenso(c.iso)}: ${doDia.length ? `${doDia.length} agendamento${doDia.length === 1 ? '' : 's'}` : 'livre'}`}
              className={classes}
              onClick={() => aoEscolherDia(c.iso)}
              onDoubleClick={() => aoAbrirDia(c.iso)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') aoAbrirDia(c.iso);
                if (e.key === ' ') {
                  e.preventDefault();
                  aoEscolherDia(c.iso);
                }
              }}
            >
              <div className="cal__topo">
                <span className={`cal__num${c.iso === hoje ? ' cal__num--hoje' : ''}`}>{c.dia}</span>
                {doDia.length > 0 && <span className="cal__qtd">{doDia.length}</span>}
              </div>
              <div className="cal__previas" aria-hidden="true">
                {doDia.slice(0, MAX_NA_CELULA).map((a) => (
                  <span key={a.id} className={`cal__previa cal__previa--${a.status}`}>
                    <i style={{ background: a.profissionalCor }} />
                    <b>{a.horaInicio}</b>
                    <span>{a.pacienteNome || a.leadNome}</span>
                  </span>
                ))}
                {doDia.length > MAX_NA_CELULA && <span className="cal__mais">+{doDia.length - MAX_NA_CELULA} mais</span>}
              </div>
            </div>
          );
        })}
      </div>

      <aside className="cal__painel" aria-label={`Agendamentos de ${diaPorExtenso(diaEscolhido)}`}>
        <header className="cal__painel-topo">
          <div>
            <h3>{diaPorExtenso(diaEscolhido)}</h3>
            <p>
              {doDiaEscolhido.length === 0
                ? 'Nenhum agendamento'
                : `${doDiaEscolhido.length} agendamento${doDiaEscolhido.length === 1 ? '' : 's'}${ativosNoDia !== doDiaEscolhido.length ? ` · ${ativosNoDia} ativo${ativosNoDia === 1 ? '' : 's'}` : ''}`}
            </p>
          </div>
        </header>
        <div className="cal__acoes">
          <Botao tamanho="sm" onClick={() => aoMarcar(diaEscolhido)} disabled={diaEscolhido < hoje}>
            + Marcar neste dia
          </Botao>
          <Botao variante="secundario" tamanho="sm" onClick={() => aoAbrirDia(diaEscolhido)}>
            Ver em lista
          </Botao>
        </div>

        <div className="cal__itens">
          {doDiaEscolhido.length === 0 && <p className="cal__vazio">Dia livre. Use “Marcar neste dia” para agendar.</p>}
          {doDiaEscolhido.map((a) => (
            <button key={a.id} type="button" className={`cal__item cal__item--${a.status}`} onClick={() => aoEscolherOS(a.id)}>
              <span className="cal__item-hora">
                <b>{a.horaInicio}</b>
                <small>{a.horaFim}</small>
              </span>
              <span className="cal__item-barra" style={{ background: a.profissionalCor }} aria-hidden="true" />
              <span className="cal__item-texto">
                <strong>{a.pacienteNome || a.leadNome}</strong>
                <small>{a.servicoNome}</small>
                <small className="cal__item-medico">{a.profissionalNome}</small>
              </span>
              <span className="cal__item-status">
                <Status valor={a.status} />
              </span>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}
